import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { ERROR_BASE_DATE } from '~/constants/organization_notification';
import {
  GetUnreadYakumaruRequestErrorNotificationsDocument,
  MarkOrganizationNotificationDocument,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { yakumaruRequestErrorNotificationsState } from '~/state/layouts/SharedAppShell/atoms';

export const useSubscriptionYakumaruRequestErrorNotifications = () => {
  const [{ notifications, loaded }, setState] = useAtom(yakumaruRequestErrorNotificationsState);
  const [notificationCreatedEvent, clear] = useSubscriptions('OrganizationNotification', [
    'created',
  ]);
  const [markNotification] = useMutation(MarkOrganizationNotificationDocument);
  const [getNotifications, { data }] = useLazyQuery(
    GetUnreadYakumaruRequestErrorNotificationsDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  const handleClose = useCallback(
    async (notificationId: string) => {
      const newNotifications = notifications.filter(
        (notification) => notification.id !== notificationId,
      );
      setState((_state) => ({ ..._state, notifications: newNotifications }));

      const notification = notifications.find((n) => n.id === notificationId);

      if (notification) {
        await markNotification({
          variables: {
            input: {
              organizationNotificationId: notification.id,
            },
          },
        });
      }
    },
    [markNotification, notifications, setState],
  );

  useEffect(() => {
    if (notificationCreatedEvent) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
      clear();
    }
  }, [clear, notificationCreatedEvent, getNotifications]);

  useEffect(() => {
    if (!loaded) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
    }
  }, [getNotifications, loaded]);

  useEffect(() => {
    if (data === undefined) return;

    const notifications = data
      ? getMe(data)?.organization.unreadOrganizationNotifications.nodes || []
      : [];

    setState((_state) => ({
      ..._state,
      notifications,
      loaded: true,
    }));
  }, [data, setState]);

  return {
    handleClose,
  };
};
